import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Descriptions, Tag, Button, Space, Table, Modal, message, Typography, Alert, Select,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getDeduplicationJob,
  mergeDeduplicationGroup,
  skipDeduplicationGroup,
  cancelDeduplicationJob,
  type DeduplicationJob,
  type DuplicateGroup,
} from '@/api/deduplicationApi';
import { useAuth } from '@/hooks/useAuth';

const { Title } = Typography;

const statusColors: Record<string, string> = {
  pending: 'default',
  scanning: 'processing',
  review: 'blue',
  merging: 'processing',
  completed: 'green',
  failed: 'red',
  cancelled: 'orange',
};

const groupStatusColors: Record<string, string> = {
  pending: 'default',
  merged: 'green',
  skipped: 'orange',
  rejected: 'red',
};

export default function DeduplicationJobDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [job, setJob] = useState<DeduplicationJob | null>(null);
  const [loading, setLoading] = useState(true);
  const [masterRecordIds, setMasterRecordIds] = useState<Record<string, string>>({});

  const canWrite = hasPermission('data_management', 'write');

  const loadJob = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await getDeduplicationJob(id);
      if (res.data?.success && res.data.data) {
        setJob(res.data.data);
      }
    } catch {
      message.error(t('app.error'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadJob();
  }, [loadJob]);

  const handleMerge = async (group: DuplicateGroup) => {
    const masterId = masterRecordIds[group.group_id] || group.master_record_id;
    if (!masterId) {
      message.warning(t('data_management.dedup.job.select_master_first', 'Select a master record first'));
      return;
    }
    try {
      await mergeDeduplicationGroup(id!, {
        group_id: group.group_id,
        master_record_id: masterId,
      });
      message.success(t('data_management.dedup.job.merge_success'));
      loadJob();
    } catch {
      message.error(t('app.error'));
    }
  };

  const handleSkip = async (groupId: string) => {
    try {
      await skipDeduplicationGroup(id!, groupId);
      message.success(t('data_management.dedup.job.skip_success'));
      loadJob();
    } catch {
      message.error(t('app.error'));
    }
  };

  const handleCancel = () => {
    Modal.confirm({
      title: t('app.confirm'),
      content: t('data_management.dedup.job.cancel'),
      okText: t('app.yes'),
      cancelText: t('app.no'),
      okType: 'danger',
      onOk: async () => {
        try {
          await cancelDeduplicationJob(id!);
          message.success(t('data_management.dedup.job.cancel_success'));
          loadJob();
        } catch {
          message.error(t('app.error'));
        }
      },
    });
  };

  if (loading || !job) return null;

  const groupColumns = [
    {
      title: t('data_management.dedup.job.field.group_id', 'Group'),
      dataIndex: 'group_id',
      key: 'group_id',
      width: 160,
      ellipsis: true,
    },
    {
      title: t('data_management.dedup.job.field.records', 'Records'),
      dataIndex: 'record_ids',
      key: 'record_ids',
      width: 80,
      render: (ids: string[]) => ids.length,
    },
    {
      title: t('data_management.dedup.job.field.similarity', 'Similarity'),
      dataIndex: 'similarity_score',
      key: 'similarity_score',
      width: 100,
      render: (v: number) => `${(v * 100).toFixed(0)}%`,
    },
    {
      title: t('data_management.dedup.job.field.match_fields', 'Match Fields'),
      dataIndex: 'match_fields',
      key: 'match_fields',
      render: (fields: string[]) => fields.join(', '),
    },
    {
      title: t('data_management.dedup.job.field.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (v: string) => (
        <Tag color={groupStatusColors[v] ?? 'default'}>
          {t(`data_management.dedup.job.group_statuses.${v}`, v)}
        </Tag>
      ),
    },
    {
      title: t('data_management.dedup.job.field.master_record', 'Master Record'),
      key: 'master',
      width: 200,
      render: (_: unknown, record: DuplicateGroup) => {
        if (record.status !== 'pending') {
          return record.master_record_id || '-';
        }
        return (
          <Select
            size="small"
            style={{ width: 180 }}
            value={masterRecordIds[record.group_id] || record.master_record_id || undefined}
            onChange={(v) => setMasterRecordIds((prev) => ({ ...prev, [record.group_id]: v }))}
            options={record.record_ids.map((rid) => ({ value: rid, label: rid.substring(0, 12) + '...' }))}
          />
        );
      },
    },
    ...(canWrite && job.status === 'review'
      ? [
          {
            title: t('app.actions'),
            key: 'actions',
            width: 160,
            render: (_: unknown, record: DuplicateGroup) => {
              if (record.status !== 'pending') return null;
              return (
                <Space>
                  <Button size="small" type="primary" onClick={() => handleMerge(record)}>
                    {t('data_management.dedup.job.merge')}
                  </Button>
                  <Button size="small" onClick={() => handleSkip(record.group_id)}>
                    {t('data_management.dedup.job.skip')}
                  </Button>
                </Space>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/data-management/dedup-jobs')}>
          {t('app.back')}
        </Button>
        {canWrite && ['scanning', 'review'].includes(job.status) && (
          <Button danger onClick={handleCancel}>
            {t('data_management.dedup.job.cancel')}
          </Button>
        )}
      </Space>

      <Title level={4}>{job.rule_name}</Title>

      {job.error_message && (
        <Alert type="error" message={job.error_message} style={{ marginBottom: 16 }} showIcon />
      )}

      <Descriptions bordered column={2} style={{ marginBottom: 24 }}>
        <Descriptions.Item label={t('data_management.dedup.job.field.rule_name')}>{job.rule_name}</Descriptions.Item>
        <Descriptions.Item label={t('data_management.dedup.job.field.status')}>
          <Tag color={statusColors[job.status] ?? 'default'}>
            {t(`data_management.dedup.job.statuses.${job.status}`, job.status)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.dedup.job.field.target_module')}>
          {t(`data_management.import.modules.${job.target_module}`, job.target_module)}
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.dedup.job.field.owner')}>{job.owner_id}</Descriptions.Item>
        <Descriptions.Item label={t('data_management.dedup.job.field.total_scanned')}>
          {job.total_scanned > 0 ? job.total_scanned.toLocaleString() : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.dedup.job.field.duplicate_groups_found')}>
          {job.duplicate_groups_found > 0 ? job.duplicate_groups_found.toLocaleString() : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.dedup.job.field.records_merged')}>
          {job.records_merged > 0 ? job.records_merged.toLocaleString() : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.dedup.job.field.records_skipped')}>
          {job.records_skipped > 0 ? job.records_skipped.toLocaleString() : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.dedup.job.field.started_at')}>
          {job.started_at ? new Date(job.started_at).toLocaleString() : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('data_management.dedup.job.field.completed_at')}>
          {job.completed_at ? new Date(job.completed_at).toLocaleString() : '-'}
        </Descriptions.Item>
      </Descriptions>

      {job.duplicate_groups?.length > 0 && (
        <>
          <Title level={5}>
            {t('data_management.dedup.job.field.duplicate_groups_found')} ({job.duplicate_groups.length})
          </Title>
          <Table
            dataSource={job.duplicate_groups}
            columns={groupColumns}
            rowKey="group_id"
            pagination={{ pageSize: 20 }}
            size="small"
          />
        </>
      )}
    </div>
  );
}
